import { getReporterAccessToken, ReporterAuthenticationError } from './reporter';

const REPORTING_URLS = [
  'https://admin.eu.elba.security/api/phishing-webhook/report-email',
  'https://admin.us.elba.security/api/phishing-webhook/report-email',
];

export class ReportSubmissionError extends Error {
  constructor(public readonly outcome: 'refused' | 'unconfirmed' | 'authentication') {
    super(outcome);
    // This add-in compiles to ES5, where `super()` returns a plain Error. Without the prototype,
    // `instanceof` fails and every outcome shows as unconfirmed (report-outcomes.test.mjs).
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export async function reportPhishingEmail() {
  const messageId = Office.context.mailbox.item?.internetMessageId;
  const body = await getEmailBodyTextAsync();
  const sender = Office.context.mailbox.item?.sender.displayName;
  const subject = Office.context.mailbox.item?.subject;
  const recipientEmail = Office.context.mailbox.userProfile.emailAddress;

  let accessToken: string;
  let graphToken: string;
  try {
    ({ accessToken, graphToken } = await getReporterAccessToken(recipientEmail));
  } catch (error) {
    throw new ReportSubmissionError(
      error instanceof ReporterAuthenticationError ? 'authentication' : 'unconfirmed'
    );
  }

  const results = await Promise.allSettled(
    REPORTING_URLS.map(async (url) => {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'X-Reporter-Graph-Authorization': `Bearer ${graphToken}`,
        },
        body: JSON.stringify({
          emailClient: 'outlook',
          emailId: messageId,
          body: body,
          recipient: recipientEmail,
          sender,
          subject,
        }),
      });

      const data = await response.json();
      if (response.status === 200) {
        if (data.wasAnElbaTest) {
          return 'elba_test';
        }
        return 'real_phishing';
      }
      throw new ReportSubmissionError(response.status === 403 ? 'refused' : 'unconfirmed');
    })
  );

  const allErrors = results.every((result) => {
    return result.status === 'rejected';
  });

  if (allErrors) {
    const refused = results.every(
      (result) =>
        result.status === 'rejected' &&
        result.reason instanceof ReportSubmissionError &&
        result.reason.outcome === 'refused'
    );
    throw new ReportSubmissionError(refused ? 'refused' : 'unconfirmed');
  }

  // check if the email was an elba test on one of the environments
  const wasAnElbaTest = results.some((result) => {
    if (result.status === 'fulfilled') {
      return result.value === 'elba_test';
    }
    return false;
  });

  if (wasAnElbaTest) {
    return 'elba_test';
  }
  return 'real_phishing';
}

function getEmailBodyTextAsync() {
  return new Promise((resolve, reject) => {
    Office.context.mailbox.item?.body.getAsync(Office.CoercionType.Html, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value);
      } else {
        reject(result.error.message);
      }
    });
  });
}
